import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Wand2, Sparkles, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface AIQuestionGeneratorProps {
  testId: string;
  testData: {
    subject: string;
    grade: string;
    topic: string;
  };
  onQuestionsGenerated: () => void;
}

interface GeneratedQuestion {
  type: string;
  question: string;
  options?: string[];
  correct_answer?: number | boolean;
  pairs?: Array<{ left: string; right: string }>;
  puzzle_data?: any;
  blanks?: string[];
  explanation?: string;
  ai_generated: boolean;
  difficulty: string;
}

export default function AIQuestionGenerator({ testId, testData, onQuestionsGenerated }: AIQuestionGeneratorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(['multiple_choice']);
  const [numberOfQuestions, setNumberOfQuestions] = useState(5);
  const [difficulty, setDifficulty] = useState('medium');
  const [additionalContext, setAdditionalContext] = useState('');
  const [generatedQuestions, setGeneratedQuestions] = useState<GeneratedQuestion[]>([]);

  const questionTypes = [
    { id: 'multiple_choice', label: 'Multiple Choice', description: 'Traditional A, B, C, D questions' },
    { id: 'matching', label: 'Matching Pairs', description: 'Match terms with definitions' },
    { id: 'puzzle', label: 'Puzzle/Riddle', description: 'Logic puzzles and brain teasers' },
    { id: 'true_false', label: 'True/False', description: 'Statement evaluation questions' },
    { id: 'fill_blank', label: 'Fill in the Blank', description: 'Complete the sentence questions' }
  ];

  const handleTypeToggle = (typeId: string) => {
    setSelectedTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    );
  };

  const generateQuestions = async () => {
    if (selectedTypes.length === 0) {
      toast.error('Please select at least one question type');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-ai-questions', {
        body: {
          topic: testData.topic,
          grade: testData.grade,
          subject: testData.subject,
          questionTypes: selectedTypes,
          numberOfQuestions,
          difficulty,
          additionalContext: additionalContext.trim() || undefined
        }
      });

      if (error) throw error;

      setGeneratedQuestions(data.questions);
      toast.success(`Generated ${data.questions.length} questions!`);
    } catch (error) {
      console.error('Error generating questions:', error);
      toast.error('Failed to generate questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addQuestionsToTest = async () => {
    if (generatedQuestions.length === 0) return;

    setLoading(true);
    try {
      // Get current question count
      const { count } = await supabase
        .from('instructor_test_questions')
        .select('*', { count: 'exact', head: true })
        .eq('test_id', testId);

      const startOrder = (count || 0) + 1;

      // Prepare questions for insertion
      const questionsToInsert = generatedQuestions.map((q, index) => ({
        test_id: testId,
        question_order: startOrder + index,
        points: 1,
        question_type: q.type as any,
        ai_generated: true,
        ai_prompt: `${testData.topic} - ${difficulty} difficulty`,
        custom_question_data: {
          text: q.question,
          type: q.type,
          explanation: q.explanation,
          difficulty: q.difficulty,
          ...(q.options && q.correct_answer !== undefined && {
            options: q.options.map((opt, idx) => ({
              text: opt,
              is_correct: idx === q.correct_answer
            }))
          }),
          ...(q.pairs && { pairs: q.pairs }),
          ...(q.puzzle_data && { puzzle_data: q.puzzle_data }),
          ...(q.blanks && { blanks: q.blanks }),
          ...(q.correct_answer !== undefined && q.type === 'true_false' && {
            correct_answer: q.correct_answer
          })
        }
      }));

      const { error } = await supabase
        .from('instructor_test_questions')
        .insert(questionsToInsert);

      if (error) throw error;

      // Update test question count
      await supabase
        .from('instructor_tests')
        .update({ total_questions: (count || 0) + generatedQuestions.length })
        .eq('id', testId);

      toast.success(`Added ${generatedQuestions.length} questions to your test!`);
      setIsOpen(false);
      setGeneratedQuestions([]);
      onQuestionsGenerated();
    } catch (error) {
      console.error('Error adding questions to test:', error);
      toast.error('Failed to add questions to test');
    } finally {
      setLoading(false);
    }
  };

  const renderQuestionPreview = (question: GeneratedQuestion, index: number) => {
    return (
      <Card key={index} className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">
              Question {index + 1}
              <Badge variant="outline" className="ml-2 capitalize">
                {question.type.replace('_', ' ')}
              </Badge>
            </CardTitle>
            <Badge variant="secondary" className="text-xs">
              {question.difficulty}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm font-medium">{question.question}</p>
          
          {question.type === 'multiple_choice' && question.options && (
            <div className="space-y-1">
              {question.options.map((option, optIndex) => (
                <div 
                  key={optIndex}
                  className={`p-2 rounded text-xs border ${
                    optIndex === question.correct_answer 
                      ? 'border-green-200 bg-green-50 text-green-800' 
                      : 'border-gray-200'
                  }`}
                >
                  {String.fromCharCode(65 + optIndex)}. {option}
                  {optIndex === question.correct_answer && <span className="ml-1 font-medium">(Correct)</span>}
                </div>
              ))}
            </div>
          )}

          {question.type === 'matching' && question.pairs && (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Terms</p>
                {question.pairs.map((pair, idx) => (
                  <div key={idx} className="p-2 bg-blue-50 rounded text-xs border">
                    {pair.left}
                  </div>
                ))}
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Definitions</p>
                {question.pairs.map((pair, idx) => (
                  <div key={idx} className="p-2 bg-green-50 rounded text-xs border">
                    {pair.right}
                  </div>
                ))}
              </div>
            </div>
          )}

          {question.type === 'true_false' && (
            <div className="p-2 rounded text-xs border border-purple-200 bg-purple-50">
              <span className="font-medium">Answer: </span>
              <span className={question.correct_answer ? 'text-green-600' : 'text-red-600'}>
                {question.correct_answer ? 'True' : 'False'}
              </span>
            </div>
          )}

          {question.type === 'fill_blank' && question.blanks && (
            <div className="p-2 rounded text-xs border border-orange-200 bg-orange-50">
              <span className="font-medium">Answers: </span>
              {question.blanks.join(', ')}
            </div>
          )}

          {question.explanation && (
            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                <strong>Explanation:</strong> {question.explanation}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600">
          <Wand2 className="h-4 w-4 mr-2" />
          Generate with AI
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            AI Question Generator
          </DialogTitle>
        </DialogHeader>
        
        {generatedQuestions.length === 0 ? (
          <div className="space-y-6">
            {/* Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Generation Settings</CardTitle>
                <CardDescription>
                  Configure how AI should generate questions for "{testData.topic}" (Grade {testData.grade} {testData.subject})
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Number of Questions</Label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      value={numberOfQuestions}
                      onChange={(e) => setNumberOfQuestions(Number(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Difficulty Level</Label>
                    <Select value={difficulty} onValueChange={setDifficulty}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="easy">Easy</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Question Types</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {questionTypes.map((type) => (
                      <div key={type.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={type.id}
                          checked={selectedTypes.includes(type.id)}
                          onCheckedChange={() => handleTypeToggle(type.id)}
                        />
                        <div className="grid gap-1.5 leading-none">
                          <Label
                            htmlFor={type.id}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            {type.label}
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            {type.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Additional Context (Optional)</Label>
                  <Textarea
                    placeholder="Provide any specific requirements or focus areas for the questions..."
                    value={additionalContext}
                    onChange={(e) => setAdditionalContext(e.target.value)}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={generateQuestions} disabled={loading || selectedTypes.length === 0}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Questions
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium">Generated Questions</h3>
                <p className="text-sm text-muted-foreground">
                  Review and add these questions to your test
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setGeneratedQuestions([])}
                  disabled={loading}
                >
                  Generate New
                </Button>
                <Button onClick={addQuestionsToTest} disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Add to Test
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {generatedQuestions.map((question, index) => renderQuestionPreview(question, index))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}